import { useChatStore } from "../state/chat";
import { useCinematicStore } from "../state/cinematic";
import { usePanelStore } from "../state/panel";
import type { ConfirmationAction } from "../ctui/chat/types";
import { SCRIPT, type Step } from "./script";

export interface Ctrl {
  cancelled: boolean;
  paused: boolean;
  /** set to true to skip remaining steps of the current beat and jump to listen */
  skipToListen: boolean;
  /** speed multiplier — 2 means 2× speed; 0.5 means half speed. */
  speed: number;
}

function sleep(ms: number, ctrl: Ctrl): Promise<void> {
  return new Promise((resolve) => {
    let remaining = ms / ctrl.speed;
    let start = performance.now();
    const tick = () => {
      if (ctrl.cancelled || ctrl.skipToListen) return resolve();
      if (ctrl.paused) {
        start = performance.now();
        requestAnimationFrame(tick);
        return;
      }
      const now = performance.now();
      remaining -= now - start;
      start = now;
      if (remaining <= 0) return resolve();
      requestAnimationFrame(tick);
    };
    if (ms <= 0) return resolve();
    requestAnimationFrame(tick);
  });
}

function waitForActionResolution(id: string, ctrl: Ctrl): Promise<void> {
  return new Promise((resolve) => {
    const unsubscribe = useChatStore.subscribe((state) => {
      const entry = state.entries.find((e) => e.id === id);
      if (!entry || entry.kind !== "action") return;
      if (entry.state === "done" || entry.state === "overridden") {
        cleanup();
        resolve();
      }
    });

    const cancelWatch = setInterval(() => {
      if (ctrl.cancelled || ctrl.skipToListen) cleanup(true);
    }, 100);

    function cleanup(force = false) {
      unsubscribe();
      clearInterval(cancelWatch);
      if (force) resolve();
    }
  });
}

function waitForConfirmation(
  id: string,
  autoAfter: number | undefined,
  autoKey: "1" | "2" | "3" | undefined,
  ctrl: Ctrl
): Promise<ConfirmationAction | null> {
  return new Promise((resolve) => {
    const unsubscribe = useChatStore.subscribe((state, prev) => {
      const entry = state.entries.find((e) => e.id === id);
      if (!entry || entry.kind !== "confirmation") return;
      const prevEntry = prev.entries.find((e) => e.id === id);
      if (prevEntry && prevEntry.kind === "confirmation" && prevEntry.resolvedWith) return;
      if (entry.resolvedWith) {
        cleanup();
        resolve(entry.resolvedWith);
      }
    });

    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (autoAfter && autoKey) {
      const fire = () => {
        if (ctrl.cancelled || ctrl.skipToListen) return cleanup(null);
        if (ctrl.paused) {
          timeout = setTimeout(fire, 200);
          return;
        }
        const state = useChatStore.getState();
        const entry = state.entries.find((e) => e.id === id);
        if (!entry || entry.kind !== "confirmation" || entry.resolvedWith) {
          cleanup();
          return;
        }
        const action = entry.actions.find((a) => a.key === autoKey);
        if (action) {
          state.resolveConfirmation(id, action);
        }
      };
      timeout = setTimeout(fire, autoAfter / ctrl.speed);
    }

    const cancelWatch = setInterval(() => {
      if (ctrl.cancelled || ctrl.skipToListen) {
        cleanup(null);
      }
    }, 100);

    function cleanup(override?: ConfirmationAction | null) {
      unsubscribe();
      clearInterval(cancelWatch);
      if (timeout) clearTimeout(timeout);
      if (override !== undefined) resolve(override);
    }
  });
}

async function playStep(step: Step, ctrl: Ctrl): Promise<void> {
  if (ctrl.cancelled) return;
  if (ctrl.skipToListen && step.kind !== "listen") return;

  const chat = useChatStore.getState();
  const panel = usePanelStore.getState();

  switch (step.kind) {
    case "msg": {
      chat.append({ id: step.id, kind: "ripper", text: "", streaming: true });
      const msPerChar = step.msPerChar ?? 38;
      for (let i = 0; i < step.tokens.length; i++) {
        if (ctrl.cancelled || ctrl.skipToListen) {
          useChatStore.getState().appendToken(step.id, step.tokens.slice(i));
          useChatStore.getState().setStreaming(step.id, false);
          return;
        }
        const ch = step.tokens[i];
        await sleep(msPerChar, ctrl);
        useChatStore.getState().appendToken(step.id, ch);
        // Natural cadence — breathe a little after sentence / clause ends.
        if (ch === "." || ch === "?" || ch === "!") await sleep(480, ctrl);
        else if (ch === "," || ch === ";" || ch === ":") await sleep(220, ctrl);
        else if (ch === "\n") await sleep(260, ctrl);
      }
      useChatStore.getState().setStreaming(step.id, false);
      if (step.pauseAfter) await sleep(step.pauseAfter, ctrl);
      return;
    }

    case "panel": {
      panel.reveal(step.view, step.focus);
      if (step.pauseAfter) await sleep(step.pauseAfter, ctrl);
      return;
    }

    case "confirm": {
      chat.append({
        id: step.id,
        kind: "confirmation",
        title: step.title,
        body: step.body,
        actions: step.actions,
      });
      await waitForConfirmation(step.id, step.autoResolveAfter, step.autoResolveKey, ctrl);
      return;
    }

    case "act": {
      const actMs = step.actMs ?? 4200;
      chat.append({
        id: step.id,
        kind: "action",
        title: step.title,
        body: step.body,
        receipt: step.receipt,
        state: "pending",
        actMs,
      });
      await waitForActionResolution(step.id, ctrl);
      return;
    }

    case "pause":
      await sleep(step.ms, ctrl);
      return;

    case "listen": {
      useChatStore.getState().setMode("listening");
      if (step.suggestions && step.suggestions.length > 0) {
        useChatStore.getState().append({
          id: `suggestions-${Date.now()}`,
          kind: "suggestions",
          title: "try one of these or ask anything",
          prompts: step.suggestions,
        });
      }
      return;
    }
  }
}

export interface Player {
  start: () => Promise<void>;
  cancel: () => void;
  pause: () => void;
  resume: () => void;
  skip: () => void;
  /** Replay the whole cinematic from the start. */
  replay: () => Promise<void>;
  isPaused: () => boolean;
}

export function createPlayer(): Player {
  let ctrl: Ctrl = {
    cancelled: false,
    paused: false,
    skipToListen: false,
    speed: 1,
  };
  let running = false;

  const loop = async () => {
    if (running) return;
    running = true;
    for (const step of SCRIPT) {
      if (ctrl.cancelled) break;
      await playStep(step, ctrl);
    }
    running = false;
  };

  return {
    start: async () => {
      if (running) return;
      ctrl = { cancelled: false, paused: false, skipToListen: false, speed: 1 };
      useChatStore.getState().reset();
      await loop();
    },
    cancel: () => {
      ctrl.cancelled = true;
    },
    pause: () => {
      ctrl.paused = true;
      useCinematicStore.getState().setPaused(true);
    },
    resume: () => {
      ctrl.paused = false;
      useCinematicStore.getState().setPaused(false);
    },
    skip: () => {
      ctrl.skipToListen = true;
      useCinematicStore.getState().setPaused(false);
    },
    replay: async () => {
      ctrl.cancelled = true;
      await new Promise((r) => setTimeout(r, 50));
      running = false;
      ctrl = { cancelled: false, paused: false, skipToListen: false, speed: 1 };
      useChatStore.getState().reset();
      useChatStore.getState().setMode("cinematic");
      usePanelStore.getState().setFocus({ kind: "portfolio" });
      usePanelStore.getState().setView("intelligence");
      await loop();
    },
    isPaused: () => ctrl.paused,
  };
}
